import { useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import { notifications } from '@/mocks/dashboard';
import { classMasterItems, rolesMasterItems, sectionsMasterItems, type MasterItem } from '@/mocks/masters';
import { mockGlobalEvents, mockSchoolHolidays } from '@/mocks/calendarEntries';
import type { GlobalEvent, SchoolHoliday } from '@/mocks/calendarEntries';
import MasterList from '@/pages/dashboard/admin/settings/components/MasterList';
import AwarenessEventsMaster from '@/pages/dashboard/admin/school-calendar/components/AwarenessEventsMaster';
import HolidayMaster from '@/pages/dashboard/admin/school-calendar/components/HolidayMaster';

type TabKey = 'classes' | 'roles' | 'sections' | 'general' | 'calendar';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const TABS: { key: TabKey; label: string; icon: string }[] = [
    { key: 'classes', label: t('settings.classMaster'), icon: 'ri-building-4-line' },
    { key: 'roles', label: t('settings.rolesMaster'), icon: 'ri-shield-user-line' },
    { key: 'sections', label: t('settings.sections'), icon: 'ri-layout-grid-line' },
    { key: 'calendar', label: t('settings.calendarSettings'), icon: 'ri-calendar-2-line' },
    { key: 'general', label: t('settings.general'), icon: 'ri-settings-4-line' },
  ];

  const tabParam = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = tabParam && TABS.some((tb) => tb.key === tabParam) ? tabParam : 'general';

let classIdCounter = 100;
let roleIdCounter = 200;
let sectionIdCounter = 300;

  const [classes, setClasses] = useState<MasterItem[]>(classMasterItems);
  const [roles, setRoles] = useState<MasterItem[]>(rolesMasterItems);
  const [sections, setSections] = useState<MasterItem[]>(sectionsMasterItems);
  const [events, setEvents] = useState<GlobalEvent[]>(mockGlobalEvents);
  const [holidays, setHolidays] = useState<SchoolHoliday[]>(mockSchoolHolidays);

  const [calendarSubTab, setCalendarSubTab] = useState<'awareness' | 'holidays'>('awareness');

  let eventIdCounter = 500;
  let holidayIdCounter = 600;

  const setTab = (tab: TabKey) => {
    if (tab === 'general') {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const handleAddClass = useCallback((name: string) => {
    const newItem: MasterItem = { id: `class-${++classIdCounter}`, name, order: classes.length + 1 };
    setClasses((prev) => [...prev, newItem]);
  }, [classes.length]);

  const handleEditClass = useCallback((id: string, name: string) => {
    setClasses((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)));
  }, []);

  const handleDeleteClass = useCallback((id: string) => {
    setClasses((prev) => {
      const filtered = prev.filter((c) => c.id !== id);
      return filtered.map((c, i) => ({ ...c, order: i + 1 }));
    });
  }, []);

  const handleReorderClass = useCallback((id: string, direction: 'up' | 'down') => {
    setClasses((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((c) => c.id === id);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === sorted.length - 1) return prev;

      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      const newSorted = [...sorted];
      [newSorted[idx], newSorted[swapIdx]] = [newSorted[swapIdx], newSorted[idx]];
      return newSorted.map((c, i) => ({ ...c, order: i + 1 }));
    });
  }, []);

  const handleAddRole = useCallback((name: string) => {
    const newItem: MasterItem = { id: `role-${++roleIdCounter}`, name, order: roles.length + 1 };
    setRoles((prev) => [...prev, newItem]);
  }, [roles.length]);

  const handleEditRole = useCallback((id: string, name: string) => {
    setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, name } : r)));
  }, []);

  const handleDeleteRole = useCallback((id: string) => {
    setRoles((prev) => {
      const filtered = prev.filter((r) => r.id !== id);
      return filtered.map((r, i) => ({ ...r, order: i + 1 }));
    });
  }, []);

  const handleReorderRole = useCallback((id: string, direction: 'up' | 'down') => {
    setRoles((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((r) => r.id === id);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === sorted.length - 1) return prev;

      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      const newSorted = [...sorted];
      [newSorted[idx], newSorted[swapIdx]] = [newSorted[swapIdx], newSorted[idx]];
      return newSorted.map((r, i) => ({ ...r, order: i + 1 }));
    });
  }, []);

  const handleAddSection = useCallback((name: string) => {
    const newItem: MasterItem = { id: `section-${++sectionIdCounter}`, name, order: sections.length + 1 };
    setSections((prev) => [...prev, newItem]);
  }, [sections.length]);

  const handleEditSection = useCallback((id: string, name: string) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
  }, []);

  const handleDeleteSection = useCallback((id: string) => {
    setSections((prev) => {
      const filtered = prev.filter((s) => s.id !== id);
      return filtered.map((s, i) => ({ ...s, order: i + 1 }));
    });
  }, []);

  const handleReorderSection = useCallback((id: string, direction: 'up' | 'down') => {
    setSections((prev) => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const idx = sorted.findIndex((s) => s.id === id);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === sorted.length - 1) return prev;

      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      const newSorted = [...sorted];
      [newSorted[idx], newSorted[swapIdx]] = [newSorted[swapIdx], newSorted[idx]];
      return newSorted.map((s, i) => ({ ...s, order: i + 1 }));
    });
  }, []);

  // Calendar event handlers
  const handleAddEvent = useCallback((data: Omit<GlobalEvent, 'id' | 'color' | 'type'>) => {
    const newEvent: GlobalEvent = { ...data, id: `ge-${++eventIdCounter}`, color: '#3B82F6', type: 'global' };
    setEvents((prev) => [...prev, newEvent]);
  }, []);
  const handleEditEvent = useCallback((id: string, data: Omit<GlobalEvent, 'id' | 'color' | 'type'>) => {
    setEvents((prev) => prev.map((e) => e.id === id ? { ...e, ...data } : e));
  }, []);
  const handleDeleteEvent = useCallback((id: string) => {
    setEvents((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // Holiday handlers
  const handleAddHoliday = useCallback((data: Omit<SchoolHoliday, 'id' | 'color' | 'type'>) => {
    const newHoliday: SchoolHoliday = {
      ...data,
      id: `sh-${++holidayIdCounter}`,
      color: data.holidayType === 'Discretionary' ? '#F97316' : '#EF4444',
      type: 'holiday',
    };
    setHolidays((prev) => [...prev, newHoliday]);
  }, []);
  const handleEditHoliday = useCallback((id: string, data: Omit<SchoolHoliday, 'id' | 'color' | 'type'>) => {
    setHolidays((prev) => prev.map((h) => h.id === id ? {
      ...h,
      ...data,
      color: data.holidayType === 'Discretionary' ? '#F97316' : '#EF4444',
    } : h));
  }, []);
  const handleDeleteHoliday = useCallback((id: string) => {
    setHolidays((prev) => prev.filter((h) => h.id !== id));
  }, []);

  return (
    <AppShell
      pageTitle={t('settings.pageTitle')}
      pageSubtitle={t('settings.pageSubtitle')}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), onClick: () => navigate('/dashboard/admin') },
        { label: t('settings.settingsBreadcrumb') },
      ]}
      notifications={notifications}
    >
      {/* Tab Switcher */}
      <div className="mb-6">
        <div className="inline-flex gap-1 p-1 bg-background-200/60 rounded-full" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              onClick={() => setTab(tab.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-label font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-background-50 text-foreground-900'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={`${tab.icon} text-base`} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'classes' && (
        <div className="page-enter">
          <MasterList
            items={classes}
            title={t('settings.classMaster')}
            description={t('settings.classMasterDescription')}
            emptyMessage={t('settings.noClassesDefined')}
            onAdd={handleAddClass}
            onEdit={handleEditClass}
            onDelete={handleDeleteClass}
            onReorder={handleReorderClass}
          />
        </div>
      )}

      {activeTab === 'roles' && (
        <div className="page-enter">
          <MasterList
            items={roles}
            title={t('settings.rolesMaster')}
            description={t('settings.rolesMasterDescription')}
            emptyMessage={t('settings.noRolesDefined')}
            onAdd={handleAddRole}
            onEdit={handleEditRole}
            onDelete={handleDeleteRole}
            onReorder={handleReorderRole}
          />
        </div>
      )}

      {activeTab === 'sections' && (
        <div className="page-enter">
          <MasterList
            items={sections}
            title={t('settings.sections')}
            description={t('settings.sectionsDescription')}
            emptyMessage={t('settings.noSectionsDefined')}
            onAdd={handleAddSection}
            onEdit={handleEditSection}
            onDelete={handleDeleteSection}
            onReorder={handleReorderSection}
          />
        </div>
      )}

      {activeTab === 'calendar' && (
        <div className="page-enter">
          {/* Calendar Settings Sub-tabs */}
          <div className="inline-flex gap-1 p-1 bg-background-200/60 rounded-full mb-6" role="tablist">
            <button
              role="tab"
              aria-selected={calendarSubTab === 'awareness'}
              onClick={() => setCalendarSubTab('awareness')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-label font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                calendarSubTab === 'awareness' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-global-line text-base" />
              {t('settings.awarenessEventsMaster')}
            </button>
            <button
              role="tab"
              aria-selected={calendarSubTab === 'holidays'}
              onClick={() => setCalendarSubTab('holidays')}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-label font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                calendarSubTab === 'holidays' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className="ri-calendar-close-line text-base" />
              {t('settings.holidayMaster')}
            </button>
          </div>

          {calendarSubTab === 'awareness' && (
            <AwarenessEventsMaster
              events={events}
              onAdd={handleAddEvent}
              onEdit={handleEditEvent}
              onDelete={handleDeleteEvent}
            />
          )}
          {calendarSubTab === 'holidays' && (
            <HolidayMaster
              holidays={holidays}
              onAdd={handleAddHoliday}
              onEdit={handleEditHoliday}
              onDelete={handleDeleteHoliday}
            />
          )}
        </div>
      )}

      {activeTab === 'general' && (
        <div className="page-enter">
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
              <i className="ri-settings-4-line text-2xl text-foreground-400" />
            </div>
            <h3 className="font-heading font-700 text-foreground-900 text-lg mb-1">{t('settings.generalSettingsTitle')}</h3>
            <p className="text-foreground-500 text-sm font-label max-w-md mx-auto">
              <span dangerouslySetInnerHTML={{ __html: t('settings.generalSettingsDescription') }} />
            </p>
            <button
              onClick={() => setTab('classes')}
              className="inline-flex items-center gap-2 mt-5 px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-building-4-line" />
              {t('settings.goToClassMaster')}
            </button>
          </div>
        </div>
      )}

      {/* Quick Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <button
          onClick={() => setTab('classes')}
          className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-colors cursor-pointer text-left"
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
            <i className="ri-building-4-line text-lg text-primary-600" />
          </div>
          <div>
            <p className="text-2xl font-heading font-700 text-foreground-900">{classes.length}</p>
            <p className="text-xs font-label text-foreground-500">{t('settings.classesDefined')}</p>
          </div>
        </button>

        <button
          onClick={() => setTab('roles')}
          className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-accent-300 transition-colors cursor-pointer text-left"
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
            <i className="ri-shield-user-line text-lg text-accent-600" />
          </div>
          <div>
            <p className="text-2xl font-heading font-700 text-foreground-900">{roles.length}</p>
            <p className="text-xs font-label text-foreground-500">{t('settings.rolesConfigured')}</p>
          </div>
        </button>

        <button
          onClick={() => setTab('sections')}
          className="flex items-center gap-3 p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer text-left"
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 flex-shrink-0">
            <i className="ri-layout-grid-line text-lg text-secondary-600" />
          </div>
          <div>
            <p className="text-2xl font-heading font-700 text-foreground-900">{sections.length}</p>
            <p className="text-xs font-label text-foreground-500">{t('settings.sectionsDefined')}</p>
          </div>
        </button>
      </div>
    </AppShell>
  );
}